// Ported from NearpodNext's `src/helpers/math/mathjax.js` (its rendering half; the library setup
// lives in mathjaxInstance.ts, which this talks to instead of reading window.MathJax).
//
// Owns the stored content format (<latex data-latex="...">) and the fallback when typesetting
// fails. Both must stay in sync with NearpodNext, which authors the content rendered here.
//
// IMPORTANT: this module does not sanitize. It assumes the caller already wrote sanitized HTML
// into the element — the fallback restores exactly that HTML and nothing else.

import mathjaxInstance, { ITypesetOptions } from './mathjaxInstance'

const LATEX_TAG = 'latex'
const LATEX_ATTRIBUTE = 'data-latex'

// Stored format -> MathJax input format: MathJax only recognises \( ... \) delimiters in text.
const rewriteLatexTags = (el: HTMLElement) => {
	el.querySelectorAll(LATEX_TAG).forEach((node) => {
		const tex = node.getAttribute(LATEX_ATTRIBUTE) || ''
		node.replaceWith(document.createTextNode(`\\(${tex}\\)`))
	})
}

// Degraded but readable: show the raw TeX source instead of a blank box.
const fallbackToRawLatex = (el: HTMLElement, originalHtml: string) => {
	// Restore the original HTML to get back the <latex> tags rewriteLatexTags already replaced.
	el.innerHTML = originalHtml
	el.querySelectorAll(LATEX_TAG).forEach((node) => {
		const tex = node.getAttribute(LATEX_ATTRIBUTE) || ''
		node.replaceWith(document.createTextNode(tex))
	})
}

// Typesets the formulas already present in `el`. Never rejects — a CDN failure or a malformed
// formula falls back to raw LaTeX — so callers use .then() as the settle point.
const renderMathInto = async (
	el: HTMLElement | null,
	signal?: AbortSignal,
	options?: ITypesetOptions
): Promise<void> => {
	if (!el) return
	const originalHtml = el.innerHTML
	try {
		await mathjaxInstance.ensureLoaded()
		if (signal?.aborted) return
		rewriteLatexTags(el)
		if (signal?.aborted) return
		await mathjaxInstance.typeset(el, options)
	} catch {
		if (signal?.aborted) return
		fallbackToRawLatex(el, originalHtml)
	}
}

const renderMathClear = (el: HTMLElement | null) => {
	if (!el) return
	mathjaxInstance.clear(el)
}

export { renderMathInto, renderMathClear }
